/**
 * Extra guidance shown under a data source parameter in the "Add Data Source"
 * wizard, for the cases where the integration's own field description (coming
 * from Membrane's schema) does not explain why the option a user expects is
 * missing from the picker.
 *
 * Keyed by Membrane integration key, then by the parameter name normalized to
 * lowercase alphanumerics, so a single entry matches whichever spelling the
 * schema uses (`conversationId`, `conversation_id`, …).
 *
 * Notes support one bit of inline markup: text between backticks is rendered
 * as inline code (see `noteSegments`).
 */
const notesPerIntegration: Readonly<
  Record<string, Readonly<Record<string, string>>>
> = {
  slack: {
    conversationid:
      "Only channels Routine has been added to are listed: Slack does not expose the others to apps. To use a private channel, open it in Slack and send `/invite @Routine`, then reopen this dropdown.",
  },
  "microsoft-planner": {
    planid:
      "Only plans the signed-in account is a member of are listed. Planner is a work or school account feature; a personal Microsoft account has none.",
  },
  monday: {
    boardid:
      "Only boards the connected account can see are listed. Status and due date are chosen on the next step.",
  },
  "microsoft-teams": {
    teamid:
      "Only teams the signed-in account belongs to are listed. Add the account to the team in Microsoft Teams, then reopen this dropdown.",
    channelid:
      "Only channels in the selected team are listed. Private channels appear only if the signed-in account is a member.",
  },
};

const normalizeFieldKey = (fieldKey: string): string =>
  fieldKey.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Returns the note to display for a data source parameter, or `null` when the
 * integration/field pair has none.
 */
export function getIntegrationFieldNote(
  integrationKey: string | undefined,
  fieldKey: string
): string | null {
  if (
    !integrationKey ||
    !Object.prototype.hasOwnProperty.call(notesPerIntegration, integrationKey)
  ) {
    return null;
  }

  const notes = notesPerIntegration[integrationKey];
  const key = normalizeFieldKey(fieldKey);
  return Object.prototype.hasOwnProperty.call(notes, key) ? notes[key] : null;
}

/** A run of note text, either plain or to be rendered as inline code. */
export interface NoteSegment {
  value: string;
  code: boolean;
}

/**
 * Splits a note into plain-text and inline-code segments on backtick pairs.
 * An unpaired backtick is kept as literal text.
 */
export function noteSegments(note: string): NoteSegment[] {
  const segments: NoteSegment[] = [];
  let rest = note;

  while (rest.length > 0) {
    const open = rest.indexOf("`");
    const close = open === -1 ? -1 : rest.indexOf("`", open + 1);
    if (close === -1) {
      segments.push({ value: rest, code: false });
      break;
    }

    if (open > 0) segments.push({ value: rest.slice(0, open), code: false });
    const code = rest.slice(open + 1, close);
    if (code.length > 0) segments.push({ value: code, code: true });
    rest = rest.slice(close + 1);
  }

  return segments;
}
